import React from "react";

import classnames from "classnames";
import { MedicalPlanMapping } from "Features/MedicalPlanMappings/medicalPlanMappings.types";
import { PlanSuiteCurrentCosts, PlanSuiteRenewalCosts, WithPlans } from "Features/PlanSuites/planSuites.types";
import { useCurrentAndRenewalSuites } from "RenewalSuitesComparison/hooks";
import formatCurrency from "Utils/formatCurrency";
import { BasicLoading } from "ZeroRuby";

import { ErrorHandler } from "Cobalt/ErrorHandler";
import { EllipsisTD, Table, TD, TH, TR } from "Cobalt/TableV2";
import BodyText from "Theme/BodyText";
import { Card, CardSection } from "Theme/Card";

interface CostsComparisonTableBodyProps {
  medicalPlanMapping: MedicalPlanMapping;
  currentSuite: PlanSuiteCurrentCosts & WithPlans;
  renewalSuites: (PlanSuiteRenewalCosts & WithPlans)[];
}

const CostsComparisonTableBody = ({ medicalPlanMapping, currentSuite, renewalSuites }: CostsComparisonTableBodyProps) => {
  const { name: benefitKey, items: mappingItems, planID: currentPlanID } = medicalPlanMapping;
  const orderedPlans = [];
  const orderedCosts = [];
  const currentPlan = currentSuite.plans.find(({ id }) => id === currentPlanID);
  orderedPlans.push(currentPlan);
  const currentPlanCost = currentSuite.currentPlanCosts[currentPlanID];
  orderedCosts.push(currentPlanCost);
  mappingItems.forEach(({ planSuiteID, planID }) => {
    const renewalSuite = renewalSuites.find(({ id }) => id === planSuiteID);
    const renewalPlan = renewalSuite.plans.find(({ id }) => id === planID);
    orderedPlans.push(renewalPlan);
    const renewalPlanCost = renewalSuite.renewalPlanCosts[planID];
    orderedCosts.push(renewalPlanCost);
  });

  const employeesEnrolledCount = Object.values(currentSuite.currentEmployeeCosts).filter(({ planID }) => planID === currentPlanID).length;
  const currentCompanyContribution = orderedCosts[0].companyContribution;
  return (
    <tbody>
      <TR>
        <TD colSpan={99} align="left">
          <BodyText variant="h2">
            {benefitKey} - {employeesEnrolledCount} {employeesEnrolledCount === 1 ? "employee" : "employees"} enrolled
          </BodyText>
        </TD>
      </TR>
      <TR bgColor="gray">
        <TH width="20%">&nbsp;</TH>
        <TH width="20%">Current Plan</TH>
        {renewalSuites.map((suite, index) => (
          <TH width="20%" key={index} align="left">
            {suite.shortName}
          </TH>
        ))}
      </TR>
      <TR>
        <TD>Plan Name</TD>
        {orderedPlans.map((plan, index) => (
          <EllipsisTD key={index} align="center">
            {plan?.full_display_name}
          </EllipsisTD>
        ))}
      </TR>
      <TR>
        <TD>Monthly Total Premium</TD>
        {orderedCosts.map((planCost, index) => (
          <TD key={index} align="right">
            <span
              className={classnames({
                "text-success": planCost?.premium < orderedCosts[0]?.premium,
              })}
            >
              {formatCurrency(planCost?.premium)}
            </span>
          </TD>
        ))}
      </TR>
      <TR>
        <TD>Monthly Total Company Contributions</TD>
        {orderedCosts.map(({ companyContribution }, index) => {
          return (
            <TD key={index} align="right">
              <span
                className={classnames({
                  "text-success": companyContribution < currentCompanyContribution,
                })}
              >
                {formatCurrency(companyContribution)}
              </span>
            </TD>
          );
        })}
      </TR>
      <TR withBorder={false}>
        <TD>Monthly Total Employee Deductions</TD>
        {orderedCosts.map(({ employeeDeduction }, index) => {
          return (
            <TD key={index} align="right">
              <span
                className={classnames({
                  "text-success": employeeDeduction < orderedCosts[0]?.employeeDeduction,
                })}
              >
                {formatCurrency(employeeDeduction)}
              </span>
            </TD>
          );
        })}
      </TR>
    </tbody>
  );
};

type CostsComparisonProps = {
  medicalPlanMappings: MedicalPlanMapping[];
};

const CostsComparison = ({ medicalPlanMappings }: CostsComparisonProps) => {
  const { currentSuite, error, isError, isLoading, renewalSuites } = useCurrentAndRenewalSuites({ medicalPlanMapping: medicalPlanMappings[0] });
  if (isLoading) {
    return <BasicLoading />;
  }
  if (isError) {
    return (
      <CardSection>
        <ErrorHandler name="RenewalSuitesComparison" action="Get current plan suite" isError={isError} error={error} />
      </CardSection>
    );
  }

  return (
    <Card>
      <CardSection>
        <Table>
          {medicalPlanMappings.map((mapping, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <tbody>
                  <TR withBorder={false}>
                    <TD colSpan={99}>&nbsp;</TD>
                  </TR>
                </tbody>
              )}
              <CostsComparisonTableBody medicalPlanMapping={mapping} currentSuite={currentSuite} renewalSuites={renewalSuites} />
            </React.Fragment>
          ))}
        </Table>
      </CardSection>
    </Card>
  );
};

export default CostsComparison;
